import { Controller } from 'stimulus';
import { get } from '@rails/request.js';

const SHOW_DEBOUNCE_MS = 150;
const CACHE_TTL_MS = 3000;

export default class extends Controller {
  static targets = ['duration', 'image', 'progressBar', 'videoName'];
  static values = { categorySlug: String };
  static outlets = ['moodboard--drawer--learn-button'];

  #showTimeout;
  #wantsVisible = false;
  #cachedData = null;
  #cachedAt = 0;

  keepOpen() {
    if (this.hasMoodboardDrawerLearnButtonOutlet) {
      this.moodboardDrawerLearnButtonOutlet.keepOpen();
    }
  }

  scheduleClose() {
    if (this.hasMoodboardDrawerLearnButtonOutlet) {
      this.moodboardDrawerLearnButtonOutlet.scheduleClose();
    }
  }

  show() {
    clearTimeout(this.#showTimeout);
    this.#wantsVisible = true;

    this.#showTimeout = setTimeout(async () => {
      if ((await this.#populate()) && this.#wantsVisible) {
        this.#showIndicator();
      }
    }, SHOW_DEBOUNCE_MS);
  }

  close() {
    clearTimeout(this.#showTimeout);
    this.#wantsVisible = false;
    this.#hideIndicator();
  }

  openVideoLibraryModal() {
    this.#hideIndicator();

    document.getElementById('video-library-modal').showModal();
    document.dispatchEvent(new Event('video-library:resume'));
  }

  async #populate() {
    if (this.#cachedAt && Date.now() - this.#cachedAt < CACHE_TTL_MS) {
      if (!this.#cachedData) {
        return false;
      }
      this.#applyData(this.#cachedData);
      return true;
    }

    const { time_watched: timeWatched, video_id: videoId } = await this.#getVideoView();

    if (!videoId) {
      this.#cachedData = null;
      this.#cachedAt = Date.now();
      return false;
    }

    const {
      duration,
      image_url: imageUrl,
      name: videoName,
    } = await this.#getVideo({ id: videoId });

    if (!duration || !imageUrl || !videoName) {
      return false;
    }

    const data = { duration, imageUrl, videoName, timeWatched };
    this.#cachedData = data;
    this.#cachedAt = Date.now();
    this.#applyData(data);

    return true;
  }

  #applyData({ duration, imageUrl, videoName, timeWatched }) {
    const progressBarPercentage = this.#calculateProgressBarPercentage({ duration, timeWatched });

    this.durationTarget.innerText = Math.round((duration || 0) / 60);
    this.imageTarget.src = imageUrl || '';
    this.imageTarget.alt = videoName || '';
    this.progressBarTarget.style.width = `${progressBarPercentage}%`;
    this.videoNameTarget.textContent = videoName || '';
  }

  async #getVideo({ id }) {
    const response = await get(`/api/df/videos/${id}`);

    if (response.ok) {
      return await response.json;
    } else {
      console.error(`Unable to fetch video with id ${id}:`, response.statusCode);

      return {};
    }
  }

  async #getVideoView() {
    const response = await get('/api/df/video_view', {
      query: { category_slug: this.categorySlugValue },
    });

    if (response.ok && response.statusCode !== 204) {
      return await response.json;
    }

    if (!response.ok) {
      console.error('Unable to fetch video view for designer:', response.statusCode);
    }

    return {};
  }

  #calculateProgressBarPercentage({ duration, timeWatched }) {
    if (!duration || !timeWatched) {
      return 0;
    }

    return Math.min((timeWatched * 100) / duration, 100);
  }

  #hideIndicator() {
    this.element.classList.add('video-indicator--hidden');
    this.element.classList.remove('video-indicator--visible');
  }

  #showIndicator() {
    this.element.classList.add('video-indicator--visible');
    this.element.classList.remove('video-indicator--hidden');
  }
}
